'use client';

import { useEffect, useState, useCallback } from 'react';
import { FileText, MapPin } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { Skeleton } from '@/components/ui/skeleton';

type Props = {
  mediaId?: string;
  url?: string;
  messageType: string;
  caption?: string | null;
  filename?: string | null;
  isOutbound?: boolean;
  location?: {
    latitude: number;
    longitude: number;
  };
};

export function MediaMessage({ mediaId, url, messageType, caption, filename, isOutbound, location }: Props) {
  const { t } = useTranslation('conversations');
  const [mediaUrl, setMediaUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);

  const handleLoadError = useCallback(() => {
    setLoadFailed(true);
  }, []);

  useEffect(() => {
    // Use direct URL if provided, otherwise use mediaId to construct proxy URL
    if (url) {
      setMediaUrl(url);
      setLoading(false);
      setLoadFailed(false);
    } else if (mediaId) {
      setMediaUrl(`/api/media/${mediaId}`);
      setLoading(false);
      setLoadFailed(false);
    } else {
      setLoadFailed(true);
      setLoading(false);
    }
  }, [mediaId, url]);

  // Handle location messages separately (they don't need mediaUrl)
  if (messageType === 'location' && location) {
    const mapsLink = `https://www.google.com/maps/search/?api=1&query=${location.latitude}%2C${location.longitude}`;

    return (
      <a
        href={mapsLink}
        target="_blank"
        rel="noopener noreferrer"
        className="flex items-center gap-3 p-4 bg-chat-elevated rounded-lg border border-chat-border-soft hover:bg-chat-hover transition-colors cursor-pointer"
      >
        <div className="flex-shrink-0 w-12 h-12 bg-chat-accent rounded-full flex items-center justify-center">
          <MapPin className="h-6 w-6 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-chat-text mb-0.5">{t('media.viewLocation')}</p>
          <p className="text-xs text-chat-text-muted truncate">
            {location.latitude.toFixed(6)}, {location.longitude.toFixed(6)}
          </p>
        </div>
      </a>
    );
  }

  if (loading) {
    return (
      <div className="w-64 h-48 rounded flex items-center justify-center">
        <Skeleton className="w-full h-full" />
      </div>
    );
  }

  if (loadFailed || !mediaUrl) {
    return (
      <div className="w-64 h-48 bg-chat-panel rounded flex items-center justify-center">
        <p className={cn('text-sm', isOutbound ? 'text-green-100' : 'text-chat-text-muted')}>
          {t('media.unavailable')}
        </p>
      </div>
    );
  }

  return (
    <div>
      {messageType === 'image' && (
        <img
          src={mediaUrl}
          alt={caption || t('media.imageAlt')}
          className="rounded max-w-full h-auto max-h-96"
          onError={handleLoadError}
        />
      )}

      {messageType === 'video' && (
        <video
          src={mediaUrl}
          controls
          className="rounded max-w-full h-auto max-h-96"
          onError={handleLoadError}
        />
      )}

      {messageType === 'audio' && (
        <div className="min-w-[250px]">
          <audio
            src={mediaUrl}
            controls
            className="w-full h-10"
            onError={handleLoadError}
            style={{ minWidth: '250px' }}
          />
        </div>
      )}

      {messageType === 'document' && (
        <a
          href={mediaUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-2 text-sm underline cursor-pointer hover:opacity-80 transition-opacity text-chat-accent"
        >
          <FileText className="h-4 w-4" />
          {filename || t('media.downloadDocument')}
        </a>
      )}
    </div>
  );
}
